import { PaperPlaneRightIcon } from "@phosphor-icons/react/dist/csr/PaperPlaneRight";
import { Button } from "@volca/ui/components/ui/button";
import { Checkbox } from "@volca/ui/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@volca/ui/components/ui/dialog";
import { Spinner } from "@volca/ui/components/ui/spinner";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import type { IPayrollSheetApprovalAccessKey } from "@/payroll/payroll.d";
import { MissingTechApprovalWarning } from "./missing-tech-approval-warning";
import type { PayrollStatementNotificationChannel } from "./types";

/**
 * The gate is soft on purpose, and the copy has to stay soft with it. The title
 * keeps naming what the dialog does — swapping it for a warning turns a
 * confirmation into what looks like a failure — and only the button changes, so
 * that sending past a missing approval is a thing you knowingly say yes to.
 */
export const getSendConfirmationCopy = (
  missingApprovalCount: number,
  renotify = false,
) =>
  renotify
    ? {
        title: "Renotify technicians?",
        confirmLabel: missingApprovalCount > 0 ? "Renotify anyway" : "Renotify",
      }
    : {
        title: "Publish PayPacks?",
        confirmLabel:
          missingApprovalCount > 0 ? "Publish anyway" : "Publish PayPacks",
      };

/** What the send does, for the channels it will use. */
export const getSendSummaryCopy = ({
  renotify,
  emailSelected,
}: {
  renotify: boolean;
  emailSelected: boolean;
}) => {
  if (renotify)
    return emailSelected
      ? "They already see their current figures. This emails them again so they know to take another look."
      : "They already see their current figures. This texts them again so they know to take another look.";
  return emailSelected
    ? "Each technician can see their own PayPack and gets an email about it. It keeps up with later changes on its own; renotify anytime to let them know."
    : "Each technician can see their own PayPack. It keeps up with later changes on its own; renotify anytime to let them know.";
};

/**
 * Who gets a text, when texts are part of the send. Without email, anyone who
 * can't be texted isn't told at all, so the copy says so.
 */
export const getTextCopy = (
  textCount: number,
  selectedCount: number,
  emailSelected = true,
  unreachedCount = selectedCount - textCount,
) => {
  if (emailSelected) {
    if (textCount === 0)
      return selectedCount === 1
        ? "They have no phone number that can be texted, so this goes by email only."
        : "None of them have a phone number that can be texted, so this goes by email only.";
    if (textCount === selectedCount)
      return selectedCount === 1
        ? "They also get a text with a link to their PayPack."
        : "They also each get a text with a link to their PayPack.";
    return `${textCount} of ${selectedCount} also get a text with a link to their PayPack; the rest have no phone number to text or opted out.`;
  }
  if (textCount === selectedCount)
    return selectedCount === 1
      ? "They get a text with a link to their PayPack."
      : "Each of them gets a text with a link to their PayPack.";
  if (unreachedCount === selectedCount)
    return selectedCount === 1
      ? "They have no phone number that can be texted, so they won't be notified."
      : "None of them have a phone number that can be texted, so nobody will be notified.";
  const texted = `${textCount} of ${selectedCount} get a text with a link to their PayPack`;
  return unreachedCount > 0
    ? `${texted}; ${unreachedCount} won't be notified, with no phone number to text or opted out.`
    : `${texted}.`;
};

/**
 * On a text-only send, the technicians still emailed because the email has their
 * account setup link. Null when there are none.
 */
export const getSetupEmailCopy = (
  setupCount: number,
  selectedCount: number,
) => {
  if (setupCount === 0) return null;
  if (selectedCount === 1)
    return "They still need to set up their account, so they also get an email with the setup link.";
  return setupCount === 1
    ? "1 of them still needs to set up their account, so they also get an email with the setup link."
    : `${setupCount} of them still need to set up their account, so they also get an email with the setup link.`;
};

const CHANNEL_OPTIONS: {
  channel: PayrollStatementNotificationChannel;
  label: string;
}[] = [
  { channel: "email", label: "Email" },
  { channel: "text", label: "Text" },
];

export const SendConfirmationDialog = ({
  open,
  isSending,
  isSavingNote,
  sendDisabledReason,
  periodLabel,
  selectedEligibleCount,
  renotify = false,
  eligibleCount,
  textCount = null,
  setupCount = 0,
  unreachedCount,
  channels = null,
  onChannelsChange,
  missingReadyForEmailAccessKeys,
  onOpenChange,
  onSend,
}: {
  open: boolean;
  isSending: boolean;
  isSavingNote: boolean;
  sendDisabledReason: string | null;
  periodLabel: string;
  selectedEligibleCount: number;
  /** Everyone selected already has a published PayPack for the period. */
  renotify?: boolean;
  eligibleCount: number;
  /**
   * How many of the selected technicians also get a text. Null when the business
   * does not text PayPacks, which keeps the line out entirely.
   */
  textCount?: number | null;
  /** Selected technicians who still need to set up their account. */
  setupCount?: number;
  /** On a text-only send, selected technicians it reaches by no channel at all. */
  unreachedCount?: number;
  /**
   * The channels this send will use, chosen here. Null when there is nothing to
   * choose (the business only emails), which hides the choice.
   */
  channels?: PayrollStatementNotificationChannel[] | null;
  onChannelsChange?: (channels: PayrollStatementNotificationChannel[]) => void;
  missingReadyForEmailAccessKeys: IPayrollSheetApprovalAccessKey[];
  onOpenChange: (open: boolean) => void;
  onSend: () => void;
}) => {
  const confirmationCopy = getSendConfirmationCopy(
    missingReadyForEmailAccessKeys.length,
    renotify,
  );
  const emailSelected = !channels || channels.includes("email");
  const textSelected = channels?.includes("text") ?? false;
  const noChannel = channels !== null && channels.length === 0;
  const toggleChannel = (
    channel: PayrollStatementNotificationChannel,
    checked: boolean,
  ) => {
    if (!channels) return;
    onChannelsChange?.(
      CHANNEL_OPTIONS.map((option) => option.channel).filter((option) =>
        option === channel ? checked : channels.includes(option),
      ),
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && !isSending && !isSavingNote) onOpenChange(false);
      }}
    >
      {/* Wider than the 449px default: the summary row, the paragraph and the
          warning callout all read as cramped columns at that width. */}
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{confirmationCopy.title}</DialogTitle>
          <DialogDescription className="sr-only">
            {renotify ? "Renotify technicians" : "Publish PayPacks"}
          </DialogDescription>
        </DialogHeader>
        <Stack align="start" gap={4}>
          <Stack
            align="start"
            direction="row"
            gap={6}
            className="w-full rounded-lg border border-line bg-neutral px-4 py-3.5"
          >
            <Stack align="start" gap={1} className="min-w-0 flex-1">
              <Text
                variant="caption"
                className="font-medium tracking-widest text-brand uppercase"
              >
                Pay period
              </Text>
              <Text variant="label">{periodLabel}</Text>
            </Stack>
            <Stack align="start" gap={1} className="shrink-0">
              <Text
                variant="caption"
                className="font-medium tracking-widest text-brand uppercase"
              >
                Technicians
              </Text>
              <Text variant="label">
                {selectedEligibleCount} of {eligibleCount}
              </Text>
            </Stack>
          </Stack>
          {channels ? (
            <Stack
              direction="row"
              align="center"
              gap={6}
              className="flex-wrap"
              role="group"
              aria-label="Notify by"
            >
              <Text variant="label">Notify by</Text>
              {CHANNEL_OPTIONS.map(({ channel, label }) => (
                <label
                  key={channel}
                  htmlFor={`notify-by-${channel}`}
                  className="flex cursor-pointer items-center gap-2"
                >
                  <Checkbox
                    id={`notify-by-${channel}`}
                    checked={channels.includes(channel)}
                    disabled={isSending || isSavingNote}
                    onCheckedChange={(checked) =>
                      toggleChannel(channel, checked === true)
                    }
                  />
                  <Text>{label}</Text>
                </label>
              ))}
            </Stack>
          ) : null}
          {noChannel ? (
            <Text tone="destructive">
              Choose at least one way to notify them.
            </Text>
          ) : (
            <>
              <Text tone="muted">
                {getSendSummaryCopy({ renotify, emailSelected })}
              </Text>
              {textCount !== null && (textSelected || !channels) ? (
                <Text tone="muted">
                  {getTextCopy(
                    textCount,
                    selectedEligibleCount,
                    emailSelected,
                    unreachedCount,
                  )}
                </Text>
              ) : null}
              {!emailSelected &&
              getSetupEmailCopy(setupCount, selectedEligibleCount) ? (
                <Text tone="muted">
                  {getSetupEmailCopy(setupCount, selectedEligibleCount)}
                </Text>
              ) : null}
            </>
          )}
          {missingReadyForEmailAccessKeys.length > 0 ? (
            <MissingTechApprovalWarning
              accessKeys={missingReadyForEmailAccessKeys}
            />
          ) : null}
        </Stack>
        <DialogFooter>
          <Button
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={isSending || isSavingNote}
          >
            Cancel
          </Button>
          <Button
            disabled={
              isSending || isSavingNote || !!sendDisabledReason || noChannel
            }
            onClick={onSend}
            aria-busy={isSending || isSavingNote || undefined}
          >
            {isSending || isSavingNote ? (
              <Spinner data-icon="inline-start" aria-hidden />
            ) : (
              <PaperPlaneRightIcon
                data-icon="inline-start"
                className="h-4 w-4"
              />
            )}
            {confirmationCopy.confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
